import React, { useCallback, useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { Label } from '../ui/label';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { Check, ChevronsUpDown, Plus, X } from 'lucide-react';
import { inventoryAPI, billAPI } from '../../services/api';
import { cn } from '../../lib/utils';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList
} from '../ui/command';

// Memoized individual item row to prevent re-rendering all rows when one changes
const ItemRow = React.memo(({
    index,
    item,
    products,
    billType,
    onUpdateItem,
    onRemoveItem,
    onAddNewProduct,
    onAddNewCategory,
    errors = {}
}) => {
    const [stock, setStock] = useState(null);
    const [loadingStock, setLoadingStock] = useState(false);
    const [showPopover, setShowPopover] = useState(false);

    // Use local state for snappiness, update parent with debounce
    const [localQuantity, setLocalQuantity] = useState(item.quantitySold || 0);
    const [localPrice, setLocalPrice] = useState(item.sellingPrice || 0);
    const [availableSkus, setAvailableSkus] = useState([]);
    const [loadingSkus, setLoadingSkus] = useState(false);

    // Sync from parent but only if different
    React.useEffect(() => {
        if (item.quantitySold !== localQuantity) setLocalQuantity(item.quantitySold || 0);
    }, [item.quantitySold]);

    React.useEffect(() => {
        if (item.sellingPrice !== localPrice) setLocalPrice(item.sellingPrice || 0);
    }, [item.sellingPrice]);

    // Fast debounced update to parent (150ms is imperceptible but saves render cycles)
    React.useEffect(() => {
        const t = setTimeout(() => {
            const val = parseInt(localQuantity) || 0;
            if (val !== item.quantitySold) {
                if (item.productType !== 'SERVICE' && stock !== null && stock >= 0 && val > stock) {
                    onUpdateItem(index, 'quantitySold', stock);
                } else {
                    onUpdateItem(index, 'quantitySold', val);
                }
            }
        }, 150);
        return () => clearTimeout(t);
    }, [localQuantity, index, item.quantitySold, item.productType, stock, onUpdateItem]);

    React.useEffect(() => {
        const t = setTimeout(() => {
            const val = parseFloat(localPrice) || 0;
            if (val !== item.sellingPrice) {
                onUpdateItem(index, 'sellingPrice', val);
            }
        }, 150);
        return () => clearTimeout(t);
    }, [localPrice, index, item.sellingPrice, onUpdateItem]);

    const fetchProductData = useCallback(async (productName) => {
        setLoadingStock(true);
        setLoadingSkus(true);
        try {
            const stockRes = await inventoryAPI.getProductDetailsByName(productName);
            const qty = stockRes.data?.stockQty ?? 0;
            const pType = stockRes.data?.productType; // Get product type
            setStock(qty);

            // Only fetch SKUs if NOT Service and NOT Non-Serial (implied check or rely on empty response)
            // But we can check pType if available. 
            // Better yet, just fetch SKUs. If service, it returns empty.
            // But we need pType for validation.

            const skuRes = await billAPI.getAvailableSkuCodes(productName);
            setAvailableSkus(Array.isArray(skuRes.data) ? skuRes.data : []);

            return { qty, pType };
        } catch (err) {
            console.error("Error fetching product details:", err);
            setStock(null);
            setAvailableSkus([]);
            return { qty: null, pType: null };
        } finally {
            setLoadingStock(false);
            setLoadingSkus(false);
        }
    }, []);

    const handleProductChange = useCallback(async (value) => {
        const productId = parseInt(value);
        const product = products.find(p => p.productId === productId);
        onUpdateItem(index, 'productId', productId);

        const newPrice = product?.sellingPriceInr || 0;
        const newGst = product?.gstPercent || 0;
        onUpdateItem(index, 'sellingPrice', newPrice);
        onUpdateItem(index, 'gstPercent', newGst);
        // No local state to update anymore

        if (!product?.productName) {
            setStock(null);
            setAvailableSkus([]);
            return;
        }

        const { qty, pType } = await fetchProductData(product.productName);
        onUpdateItem(index, 'skuProductCodes', []);
        onUpdateItem(index, 'productType', pType || product.productType); // Save type

        // Validate stock and quantity behavior
        let currentQty = item.quantitySold;
        const type = pType || product.productType;

        if (type === 'SERVICE') {
            // For Service, stock is irrelevant and default to 1 on add
            if (currentQty <= 0) {
                onUpdateItem(index, 'quantitySold', 1);
            }
        } else if (availableSkus.length > 0) {
            // SKU-based products: quantity is driven by selected SKUs
            const skuQty = item.skuProductCodes?.length || 0;
            onUpdateItem(index, 'quantitySold', skuQty);
        } else {
            // Non-SKU products: allow free positive integer input, default to 1 for new selection
            if (qty <= 0) {
                onUpdateItem(index, 'quantitySold', 1);
            } else {
                const normalizedQty = currentQty > 0 ? Math.min(currentQty, qty) : 1;
                onUpdateItem(index, 'quantitySold', normalizedQty);
            }
        }
    }, [index, products, onUpdateItem, item.quantitySold, fetchProductData, availableSkus.length, item.skuProductCodes]);

    const handleSkuToggle = useCallback((sku) => {
        const currentSkus = item.skuProductCodes || [];
        const newSkus = currentSkus.includes(sku)
            ? currentSkus.filter(s => s !== sku)
            : [...currentSkus, sku];

        onUpdateItem(index, 'skuProductCodes', newSkus);
        onUpdateItem(index, 'quantitySold', newSkus.length);
    }, [index, item.skuProductCodes, onUpdateItem]);

    const handleSelectAllSkus = useCallback(() => {
        if (availableSkus.length > 0) {
            onUpdateItem(index, 'skuProductCodes', [...availableSkus]);
            onUpdateItem(index, 'quantitySold', availableSkus.length);
        }
    }, [index, availableSkus, onUpdateItem]);

    const handleClearSelectedSkus = useCallback(() => {
        onUpdateItem(index, 'skuProductCodes', []);
        const defaultQty = stock > 0 ? 1 : 0;
        onUpdateItem(index, 'quantitySold', defaultQty);
    }, [index, stock, onUpdateItem]);

    // Fetch data on initial load if product is already selected (e.g. editing)
    React.useEffect(() => {
        if (item.productId && stock === null && !loadingStock) {
            const product = products.find(p => p.productId === item.productId);
            if (product?.productName) {
                fetchProductData(product.productName);
            }
        }
    }, [item.productId, products, stock, loadingStock, fetchProductData]);

    const handleQuantityChange = (e) => {
        setLocalQuantity(e.target.value);
    };

    const handlePriceChange = (e) => {
        setLocalPrice(e.target.value);
    };

    // Show info if out of stock
    const outOfStock = stock !== null && stock <= 0;

    return (
        <Card className="relative overflow-hidden group border-muted-foreground/20 hover:border-blue-400/40 transition-all duration-300 shadow-sm hover:shadow-md bg-white">
            <CardContent className="p-4 md:p-5">
                <div className="grid grid-cols-2 md:grid-cols-12 gap-4 md:gap-3 items-start">
                    <div className="col-span-2 md:col-span-4 space-y-1">
                        <Label className="md:hidden text-muted-foreground font-semibold">Product <span className="text-destructive">*</span></Label>
                        {item.productId ? (
                            // Selected state: show as a chip with clear button
                            <div className="flex items-center gap-2">
                                <div className="flex items-center gap-1.5 flex-1 min-w-0 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 shadow-sm">
                                    <span className="h-2 w-2 rounded-full bg-blue-500 shrink-0"></span>
                                    <span className="truncate text-sm font-medium text-blue-800 flex-1">
                                        {products.find(p => p.productId === item.productId)?.productName || "Unknown Product"}
                                    </span>
                                </div>
                                <button
                                    type="button"
                                    title="Change or clear product"
                                    onClick={() => {
                                        onUpdateItem(index, 'productId', null);
                                        onUpdateItem(index, 'sellingPrice', 0);
                                        onUpdateItem(index, 'gstPercent', 0);
                                        onUpdateItem(index, 'quantitySold', 0);
                                        onUpdateItem(index, 'skuProductCodes', []);
                                    }}
                                    className="shrink-0 h-8 w-8 flex items-center justify-center rounded-lg border border-red-200 bg-red-50 text-red-500 hover:bg-red-100 hover:text-red-700 hover:border-red-300 transition-all duration-150 shadow-sm"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                        ) : (
                            // Empty state: show search combobox
                            <Popover open={showPopover} onOpenChange={setShowPopover}>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        role="combobox"
                                        aria-expanded={showPopover}
                                        className={cn(
                                            "w-full justify-between font-normal text-muted-foreground border-dashed",
                                            errors[`item_${index}_productId`] && "border-rose-400 bg-rose-50/50 ring-2 ring-rose-500/10"
                                        )}
                                    >
                                        <span className="flex items-center gap-2">
                                            <span className="text-base">🔍</span>
                                            Search product...
                                        </span>
                                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-40" />
                                    </Button>
                                </PopoverTrigger>
                                {errors[`item_${index}_productId`] && <p className="text-[10px] font-black text-rose-600 mt-1 uppercase tracking-tight">{errors[`item_${index}_productId`]}</p>}
                            <PopoverContent className="w-[300px] p-0" align="start">
                                <Command>
                                    <CommandInput placeholder="Search product..." />
                                    <div
                                        className="max-h-[200px] overflow-y-auto p-1"
                                        onWheel={(e) => e.stopPropagation()}
                                    >
                                        <CommandEmpty className="py-6 text-center text-sm">
                                            No product found.
                                            <div className="mt-4 flex flex-col gap-2 px-4">
                                                {onAddNewProduct && (
                                                    <Button type="button" size="sm" onClick={() => { setShowPopover(false); onAddNewProduct(); }}>
                                                        + Add New Product
                                                    </Button>
                                                )}
                                            </div>
                                        </CommandEmpty>
                                        <CommandGroup>
                                            {products.map((product) => (
                                                <CommandItem
                                                    key={product.productId}
                                                    value={product.productName}
                                                    onSelect={() => {
                                                        if (item.productId === product.productId) {
                                                            onUpdateItem(index, 'productId', null);
                                                            onUpdateItem(index, 'sellingPrice', 0);
                                                            onUpdateItem(index, 'gstPercent', 0);
                                                            onUpdateItem(index, 'quantitySold', 0);
                                                            onUpdateItem(index, 'skuProductCodes', []);
                                                        } else {
                                                            handleProductChange(product.productId.toString());
                                                        }
                                                        setShowPopover(false);
                                                    }}
                                                >
                                                    <Check
                                                        className={cn(
                                                            "mr-2 h-4 w-4",
                                                            item.productId === product.productId ? "opacity-100" : "opacity-0"
                                                        )}
                                                    />
                                                    {product.productName} (₹{product.sellingPriceInr})
                                                </CommandItem>
                                            ))}
                                        </CommandGroup>
                                    </div>
                                </Command>
                            </PopoverContent>
                        </Popover>
                        )} {/* end of else (empty state) */}
                        {/* Stock status badge — shown regardless of mode */}
                        {item.productId && stock !== null && !loadingStock && (
                            <div className="mt-1 flex items-center gap-1.5">
                                {stock > 0 ? (
                                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full border border-emerald-200">
                                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 inline-block"></span>
                                        {stock} in stock
                                    </span>
                                ) : (
                                    item.productType !== 'SERVICE' && (
                                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-red-50 text-red-700 px-2 py-0.5 rounded-full border border-red-200">
                                            <span className="h-1.5 w-1.5 rounded-full bg-red-500 inline-block"></span>
                                            Out of stock
                                        </span>
                                    )
                                )}
                            </div>
                        )}
                    </div>
                    <div className="col-span-1 md:col-span-2 space-y-1">
                        <Label className="md:hidden text-muted-foreground font-semibold">Quantity</Label>
                        <div className="flex flex-col gap-2">
                            <Input
                                type="number"
                                placeholder="Qty"
                                value={localQuantity}
                                onChange={handleQuantityChange}
                                onWheel={(e) => e.currentTarget.blur()}
                                min="0"
                                max={item.productType !== 'SERVICE' && stock !== null && stock > 0 ? stock : undefined}
                                disabled={
                                    !item.productId ||
                                    (item.productType !== 'SERVICE' && availableSkus.length > 0) ||
                                    (outOfStock && item.productType !== 'SERVICE')
                                }
                                className={cn(
                                    "w-full md:w-24 min-w-[4.5rem] max-w-[6.5rem] font-bold text-center bg-white dark:bg-slate-700 dark:text-white",
                                    availableSkus.length > 0 && "bg-muted cursor-not-allowed",
                                    errors[`item_${index}_quantitySold`] && "border-rose-400 bg-rose-50/50 ring-2 ring-rose-500/10"
                                )}
                            />
                            {errors[`item_${index}_quantitySold`] && <p className="text-[10px] font-black text-rose-600 mt-1 uppercase tracking-tight">{errors[`item_${index}_quantitySold`]}</p>}
                        </div>
                    </div>
                    <div className="col-span-1 md:col-span-2 space-y-1 relative">
                        <Label className="md:hidden text-muted-foreground font-semibold">Unit Price</Label>
                        <Input
                            type="number"
                            placeholder="Price"
                            value={localPrice}
                            onChange={handlePriceChange}
                            min="0"
                            step="1"
                            className={cn(
                                "w-full md:flex-1 md:min-w-[6rem] md:max-w-[12rem] text-center font-bold text-lg",
                                errors[`item_${index}_sellingPrice`] && "border-rose-400 bg-rose-50/50 ring-2 ring-rose-500/10"
                            )}
                        />
                        {errors[`item_${index}_sellingPrice`] && <p className="text-[10px] font-black text-rose-600 mt-1 uppercase tracking-tight">{errors[`item_${index}_sellingPrice`]}</p>}
                    </div>
                    <div className="col-span-2 md:col-span-3 space-y-1">
                        <Label className="md:hidden text-muted-foreground font-semibold truncate">{billType === 'Invoice' ? 'Total (Inc. GST)' : 'Total'}</Label>
                        <div className="h-10 w-full flex items-center font-bold text-blue-700 justify-end text-right bg-blue-50/50 rounded-lg overflow-hidden text-ellipsis whitespace-nowrap px-4 text-sm md:text-base border border-blue-100 shadow-inner">
                            ₹ {billType === 'Invoice'
                                ? ((parseFloat(localPrice) || 0) * (parseInt(localQuantity) || 0) * (1 + (item.gstPercent || 0) / 100)).toFixed(2)
                                : ((parseFloat(localPrice) || 0) * (parseInt(localQuantity) || 0)).toFixed(2)
                            }
                        </div>
                    </div>

                    {/* Action Column on Desktop (Delete) */}
                    <div className="hidden md:flex items-center justify-center col-span-1 self-start mt-1">
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => onRemoveItem(index)}
                            className="text-muted-foreground hover:text-red-600 hover:bg-red-50 transition-colors rounded-full"
                            title="Remove Item"
                        >
                            <X className="h-5 w-5" />
                        </Button>
                    </div>

                    {/* Action Column on Mobile (Delete) */}
                    <div className="md:hidden absolute top-2 right-2">
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => onRemoveItem(index)}
                            className="h-6 w-6 text-muted-foreground hover:text-red-600 bg-red-50/50 rounded-full shadow-sm"
                        >
                            <X className="h-3 w-3" />
                        </Button>
                    </div>
                </div>

                {/* SKU Selection Section */}
                {item.productId && (availableSkus.length > 0 || loadingSkus) && (
                    <div className="mt-4 p-3 bg-muted/30 rounded-lg border border-border/50">
                        <div className="flex items-center justify-between mb-2">
                            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                                Available Serial Numbers (SKUs)
                                {loadingSkus && <span className="animate-pulse">Loading...</span>}
                            </Label>
                            <div className="flex gap-2">
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 text-[10px] px-2"
                                    onClick={handleSelectAllSkus}
                                    disabled={loadingSkus || availableSkus.length === 0}
                                >
                                    Select All
                                </Button>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 text-[10px] px-2 text-destructive hover:text-destructive"
                                    onClick={handleClearSelectedSkus}
                                    disabled={loadingSkus || (item.skuProductCodes?.length || 0) === 0}
                                >
                                    Clear
                                </Button>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-2 max-h-[120px] overflow-y-auto p-1 scrollbar-thin">
                            {availableSkus.map(sku => {
                                const isSelected = item.skuProductCodes?.includes(sku);
                                return (
                                    <button
                                        key={sku}
                                        type="button"
                                        onClick={() => handleSkuToggle(sku)}
                                        className={cn(
                                            "px-2 py-1 rounded text-[11px] font-medium transition-all border",
                                            isSelected
                                                ? "bg-primary text-primary-foreground border-primary"
                                                : "bg-background text-foreground border-border hover:border-primary/50"
                                        )}
                                    >
                                        {sku}
                                    </button>
                                );
                            })}
                            {availableSkus.length === 0 && !loadingSkus && (
                                <span className="text-xs text-muted-foreground italic">No unique serial numbers available for this product.</span>
                            )}
                        </div>
                        {item.skuProductCodes?.length > 0 && (
                            <div className="mt-2 text-[10px] text-black font-medium flex items-center gap-1">
                                <Check className="h-3 w-3" />
                                {item.skuProductCodes.length} SKU(s) selected (Quantity auto-updated)
                            </div>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
});

const ItemForm = ({ items, billType, onAddItem, onUpdateItem, onRemoveItem, products = [], onAddNewProduct, onAddNewCategory, errors = {} }) => {
    return (
        <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-xl bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 border border-slate-200/60 dark:border-slate-700/80 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-100/50 dark:bg-blue-900/40 rounded-full blur-3xl -mr-10 -mt-10"></div>
                <div className="flex flex-col gap-1 relative z-10">
                    <h4 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-700 to-indigo-700 dark:from-cyan-300 dark:to-blue-300 w-full sm:w-auto tracking-tight">Invoice Items</h4>
                    <p className="text-xs font-medium text-slate-500 dark:text-slate-300">Scan, search, or add directly to the bill</p>
                </div>                <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                    {onAddNewCategory && (
                        <Button type="button" onClick={onAddNewCategory} variant="outline" size="sm" className="flex-1 sm:flex-none text-[#ff6f00] border-[#ff6f00]/30 bg-[#ff6f00]/5 hover:bg-[#ff6f00]/10">
                            <Plus className="mr-1 h-3 w-3" />
                            Category
                        </Button>
                    )}
                    {onAddNewProduct && (
                        <Button type="button" onClick={onAddNewProduct} variant="outline" size="sm" className="flex-1 sm:flex-none text-[#004da6] border-[#004da6]/30 bg-[#004da6]/5 hover:bg-[#004da6]/10">
                            <Plus className="mr-1 h-3 w-3" />
                            Product
                        </Button>
                    )}
                    <Button type="button" onClick={onAddItem} variant="default" size="sm" className="w-full sm:w-auto shadow-md hover:shadow-lg transition-all shrink-0 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700">
                        <Plus className="mr-2 h-4 w-4" />
                        Add New Row
                    </Button>
                </div>
            </div>

            {items.length > 0 && (
                <div className="hidden md:grid grid-cols-12 gap-3 px-6 pt-2 pb-1 text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-widest border-b border-transparent">
                    <div className="col-span-4">Product / Item <span className="text-red-500">*</span></div>
                    <div className="col-span-2">Quantity</div>
                    <div className="col-span-2">Unit Price</div>
                    <div className="col-span-3 text-right">Total {billType === 'Invoice' ? '(Inc. GST)' : ''}</div>
                    <div className="col-span-1 text-center">Action</div>
                </div>
            )}

            <div className="space-y-3">
                {items.map((item, index) => (
                    <ItemRow
                        key={index}
                        index={index}
                        item={item}
                        products={products}
                        billType={billType}
                        onUpdateItem={onUpdateItem}
                        onRemoveItem={onRemoveItem}
                        onAddNewProduct={onAddNewProduct}
                        onAddNewCategory={onAddNewCategory}
                        errors={errors}
                    />
                ))}
            </div>
        </div>
    );
};

export default React.memo(ItemForm);